import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardLayout from "@/components/DashboardLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, File, CheckCircle2, XCircle, MessageSquare, Send, Users, UserCheck, Filter, Star, Clock } from "lucide-react";
import FileDownloadButton from "@/components/FileDownloadButton";
import PdfDownloadButton from "@/components/PdfDownloadButton";
import AssignInspectorDialog from "@/components/AssignInspectorDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { statusLabel } from "@/lib/i18n";

export default function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [req, setReq] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [workshops, setWorkshops] = useState([]);
  const [selectedWs, setSelectedWs] = useState([]);
  const [rfqDialogOpen, setRfqDialogOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedWQ, setSelectedWQ] = useState(null);
  const [quoteForm, setQuoteForm] = useState({ inspection_fee: 300, logistics_fee: 200, service_fee: 250, margin: 0, validity_days: 15 });
  // Inspector assignment state
  const [inspectors, setInspectors] = useState([]);
  const [inspectorDialogOpen, setInspectorDialogOpen] = useState(false);
  // Workshop matching filters
  const [wsFilters, setWsFilters] = useState({ capability: "", city: "", material: "", min_rating: "", min_on_time: "" });
  const [capabilities, setCapabilities] = useState([]);
  const [wsLoading, setWsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/requests/${id}`);
      setReq(data);
      const { data: m } = await api.get(`/messages`, { params: { reference_type: "request", reference_id: id } });
      setMessages(m.messages || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const isOpsOrAdmin = user.role === "admin" || user.role === "operations_employee";
    if (isOpsOrAdmin) {
      api.get("/inspectors").then((r) => setInspectors(r.data.inspectors || [])).catch(() => {});
    }
  }, [user.role]);

  const isAdmin = user.role === "admin" || user.role === "operations_employee";

  const loadWorkshops = async (filters = wsFilters) => {
    setWsLoading(true);
    const params = { status: "approved" };
    if (filters.capability) params.capability = filters.capability;
    if (filters.city) params.city = filters.city;
    if (filters.material) params.material = filters.material;
    if (filters.min_rating) params.min_rating = Number(filters.min_rating);
    if (filters.min_on_time) params.min_on_time = Number(filters.min_on_time);
    const { data } = await api.get("/workshops", { params });
    setWorkshops(data.workshops || []);
    setWsLoading(false);
  };

  const openRfq = async () => {
    setRfqDialogOpen(true);
    if (!capabilities.length) {
      const { data: caps } = await api.get("/reference/capabilities");
      setCapabilities(caps.capabilities || []);
    }
    await loadWorkshops();
  };

  const applyFilters = async () => {
    await loadWorkshops(wsFilters);
    setSelectedWs([]);
  };

  const resetFilters = async () => {
    const empty = { capability: "", city: "", material: "", min_rating: "", min_on_time: "" };
    setWsFilters(empty);
    await loadWorkshops(empty);
  };

  const sendRfq = async () => {
    try {
      await api.post("/rfqs", { request_id: id, workshop_ids: selectedWs });
      toast.success("تم إرسال طلب عروض الأسعار للورش المختارة");
      setRfqDialogOpen(false); setSelectedWs([]); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async (qid) => {
    setApproving(true);
    try {
      await api.post(`/customer-quotations/${qid}/approve`);
      toast.success("تم اعتماد عرض السعر");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setApproving(false);
  };

  const reject = async (qid) => {
    try {
      await api.post(`/customer-quotations/${qid}/reject`);
      toast.success("تم رفض العرض");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try {
      await api.post("/messages", { reference_type: "request", reference_id: id, body: msg });
      setMsg("");
      const { data: m } = await api.get(`/messages`, { params: { reference_type: "request", reference_id: id } });
      setMessages(m.messages || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openQuoteDialog = (wq) => {
    setSelectedWQ(wq);
    setQuoteForm({ ...quoteForm, margin: wq.total_price * 0.25 });
    setQuoteDialogOpen(true);
  };

  const createCustomerQuote = async () => {
    try {
      await api.post("/customer-quotations", {
        request_id: id,
        selected_workshop_id: selectedWQ.workshop_id,
        workshop_quotation_id: selectedWQ.id,
        ...Object.fromEntries(Object.entries(quoteForm).map(([k, v]) => [k, Number(v)])),
      });
      toast.success("تم إنشاء عرض سعر العميل بنجاح");
      setQuoteDialogOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-amber" /></div></DashboardLayout>;
  if (!req) return null;

  const cq = req.customer_quotation;

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">تفاصيل الطلب</div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold text-brand-navy">{req.request_number}</h1>
          <StatusBadge status={req.status} />
        </div>
        <p className="text-slate-600 mt-1">{req.part_name}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* Details */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h3 className="font-bold text-brand-navy mb-4">بيانات الطلب</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-500">نوع الطلب:</span> {req.request_type}</div>
              <div><span className="text-slate-500">الكمية:</span> {req.quantity}</div>
              <div><span className="text-slate-500">المادة:</span> {req.material || "—"}</div>
              <div><span className="text-slate-500">الأبعاد:</span> {req.dimensions || "—"}</div>
              <div><span className="text-slate-500">التفاوت:</span> {req.tolerance || "—"}</div>
              <div><span className="text-slate-500">التسليم:</span> {req.delivery_date?.slice(0, 10) || "—"}</div>
              <div><span className="text-slate-500">الاستعجال:</span> {req.urgency || "عادي"}</div>
              <div><span className="text-slate-500">القطاع:</span> {req.industry || "—"}</div>
              {isAdmin && <div><span className="text-slate-500">المصنع:</span> {req.factory_name}</div>}
              <div className="col-span-2"><span className="text-slate-500">ملاحظات:</span> {req.notes || "—"}</div>
            </div>
            {req.files?.length > 0 && (
              <div className="mt-6">
                <div className="font-semibold text-brand-navy mb-2">الملفات المرفقة</div>
                <div className="grid gap-2">
                  {req.files.map((f) => (
                    <div key={f.id} className="flex items-center gap-3 border border-slate-200 rounded-md p-3">
                      <File className="w-4 h-4 text-brand-amber" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{f.filename}</div>
                        <div className="text-xs text-slate-500">{f.ext?.toUpperCase()}</div>
                      </div>
                      <FileDownloadButton fileId={f.id} filename={f.filename} data-testid={`download-file-${f.id}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="timeline">
            <h3 className="font-bold text-brand-navy mb-4">سجل الحالة</h3>
            <div className="space-y-3">
              {(req.timeline || []).map((t, i) => (
                <div key={`${t.at || ""}-${t.status || ""}-${i}`} className="flex gap-3">
                  <div className="w-2 h-2 rounded-full bg-brand-amber mt-2 shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-brand-navy">{statusLabel(t.status)}</div>
                    {t.note && <div className="text-sm text-slate-600">{t.note}</div>}
                    <div className="text-xs text-slate-400">{t.at?.slice(0, 16).replace("T", " ")}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Customer Quotation card */}
          {cq && (
            <div className="bg-white border-2 border-brand-amber rounded-lg p-6" data-testid="customer-quotation">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs font-bold tracking-widest text-brand-amber">عرض سعر MERQAV</div>
                  <div className="text-xl font-bold text-brand-navy">{cq.quotation_number}</div>
                </div>
                <StatusBadge status={cq.status} />
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div><span className="text-slate-500">المجموع الفرعي:</span> {cq.subtotal?.toLocaleString()} ر.س</div>
                <div><span className="text-slate-500">ضريبة القيمة المضافة (15%):</span> {cq.vat?.toLocaleString()} ر.س</div>
                <div className="col-span-2"><span className="text-slate-500">المبلغ الإجمالي:</span> <span className="text-2xl font-bold text-brand-amber">{cq.total?.toLocaleString()} ر.س</span></div>
                <div><span className="text-slate-500">مدة التسليم:</span> {cq.lead_time_days} يوم</div>
                <div><span className="text-slate-500">شروط الدفع:</span> {cq.payment_terms}</div>
                <div><span className="text-slate-500">الضمان:</span> {cq.warranty}</div>
                <div><span className="text-slate-500">صلاحية العرض:</span> {cq.validity_days} يوم</div>
              </div>
              {user.role === "factory_customer" && cq.status === "sent" && (
                <div className="flex flex-wrap gap-2">
                  <Button className="btn-cta" onClick={() => approve(cq.id)} disabled={approving} data-testid="approve-quotation-btn">
                    <CheckCircle2 className="w-4 h-4 me-1" /> اعتماد العرض
                  </Button>
                  <Button variant="outline" onClick={() => reject(cq.id)} data-testid="reject-quotation-btn">
                    <XCircle className="w-4 h-4 me-1" /> رفض العرض
                  </Button>
                  <PdfDownloadButton
                    endpoint={`/customer-quotations/${cq.id}/pdf`}
                    filename={`merqav-quotation-${cq.quotation_number}.pdf`}
                    data-testid="download-quotation-pdf-btn"
                  />
                </div>
              )}
              {(user.role !== "factory_customer" || cq.status !== "sent") && (
                <div className="flex gap-2">
                  <PdfDownloadButton
                    endpoint={`/customer-quotations/${cq.id}/pdf`}
                    filename={`merqav-quotation-${cq.quotation_number}.pdf`}
                    data-testid="download-quotation-pdf-btn"
                  />
                </div>
              )}
            </div>
          )}

          {/* Admin — Workshop Quotations comparison */}
          {isAdmin && req.workshop_quotations?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="workshop-quotations-comparison">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-brand-navy">مقارنة عروض الورش (سري)</h3>
                <span className="text-xs bg-red-50 text-red-700 border border-red-200 rounded px-2 py-1">داخلي فقط</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-start">الورشة</th>
                      <th className="px-3 py-2 text-start">التصنيع</th>
                      <th className="px-3 py-2 text-start">المادة</th>
                      <th className="px-3 py-2 text-start">التسليم</th>
                      <th className="px-3 py-2 text-start">الإجمالي</th>
                      <th className="px-3 py-2 text-start">المدة</th>
                      <th className="px-3 py-2 text-start"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {req.workshop_quotations.map((wq) => (
                      <tr key={wq.id} className="border-t border-slate-100" data-testid={`wq-row-${wq.quotation_number}`}>
                        <td className="px-3 py-2 font-semibold">{wq.workshop_name}</td>
                        <td className="px-3 py-2">{wq.manufacturing_cost?.toLocaleString()}</td>
                        <td className="px-3 py-2">{wq.material_cost?.toLocaleString()}</td>
                        <td className="px-3 py-2">{wq.delivery_cost?.toLocaleString()}</td>
                        <td className="px-3 py-2 font-bold text-brand-navy">{wq.total_price?.toLocaleString()} ر.س</td>
                        <td className="px-3 py-2">{wq.lead_time_days} يوم</td>
                        <td className="px-3 py-2">
                          {!cq && <Button size="sm" className="btn-cta" onClick={() => openQuoteDialog(wq)} data-testid={`select-wq-${wq.quotation_number}`}>اختيار وإعداد العرض</Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Assign Inspector — always available for admin/ops, even after CQ */}
          {isAdmin && (req.inspection_required || req.assigned_inspector) && (
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="inspector-actions">
              <h3 className="font-bold text-brand-navy mb-4 flex items-center gap-2">
                <UserCheck className="w-4 h-4" /> إدارة الفحص الفني
              </h3>
              {req.assigned_inspector && (
                <div className="mb-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 flex items-center gap-2" data-testid="assigned-inspector-info">
                  <UserCheck className="w-4 h-4" />
                  <span>مفتش مُسند: <span className="font-semibold">{inspectors.find((i) => i.id === req.assigned_inspector)?.name || req.assigned_inspector}</span></span>
                </div>
              )}
              <Button variant="outline" onClick={() => setInspectorDialogOpen(true)} data-testid="open-assign-inspector-btn">
                <UserCheck className="w-4 h-4 me-1" /> {req.assigned_inspector ? "تغيير المفتش أو تعديل الجدولة" : "إسناد مفتش"}
              </Button>
              <AssignInspectorDialog
                open={inspectorDialogOpen}
                onOpenChange={setInspectorDialogOpen}
                requestDoc={req}
                onAssigned={load}
              />
            </div>
          )}

          {/* Admin actions */}
          {isAdmin && !cq && (
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="admin-actions">
              <h3 className="font-bold text-brand-navy mb-4">إجراءات MERQAV</h3>

              <div className="flex flex-wrap gap-2">
                {/* Advanced Workshop Matching + RFQ */}
                <Dialog open={rfqDialogOpen} onOpenChange={setRfqDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-cta" onClick={openRfq} data-testid="open-rfq-dialog-btn"><Users className="w-4 h-4 me-1" /> مطابقة الورش وإرسال RFQ</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Filter className="w-5 h-5 text-brand-amber" /> مطابقة الورش المتقدمة
                      </DialogTitle>
                    </DialogHeader>

                    {/* Filter bar */}
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-3" data-testid="workshop-filters">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">القدرة التصنيعية</Label>
                          <Select value={wsFilters.capability} onValueChange={(v) => setWsFilters({ ...wsFilters, capability: v === "all" ? "" : v })}>
                            <SelectTrigger className="mt-1" data-testid="filter-capability">
                              <SelectValue placeholder="الكل" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">الكل</SelectItem>
                              {capabilities.map((c) => <SelectItem key={c.key} value={c.key}>{c.label_ar}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">المدينة</Label>
                          <Input placeholder="مثال: الرياض" value={wsFilters.city} onChange={(e) => setWsFilters({ ...wsFilters, city: e.target.value })} className="mt-1" data-testid="filter-city" />
                        </div>
                        <div>
                          <Label className="text-xs">المادة</Label>
                          <Input placeholder="مثال: stainless_steel" value={wsFilters.material} onChange={(e) => setWsFilters({ ...wsFilters, material: e.target.value })} className="mt-1" data-testid="filter-material" />
                        </div>
                        <div>
                          <Label className="text-xs">أدنى تقييم جودة</Label>
                          <Select value={wsFilters.min_rating} onValueChange={(v) => setWsFilters({ ...wsFilters, min_rating: v === "any" ? "" : v })}>
                            <SelectTrigger className="mt-1" data-testid="filter-min-rating">
                              <SelectValue placeholder="أي تقييم" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="any">أي تقييم</SelectItem>
                              <SelectItem value="3">3+ نجوم</SelectItem>
                              <SelectItem value="4">4+ نجوم</SelectItem>
                              <SelectItem value="4.5">4.5+ نجوم</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">أدنى دقة تسليم</Label>
                          <Select value={wsFilters.min_on_time} onValueChange={(v) => setWsFilters({ ...wsFilters, min_on_time: v === "any" ? "" : v })}>
                            <SelectTrigger className="mt-1" data-testid="filter-min-on-time">
                              <SelectValue placeholder="أي نسبة" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="any">أي نسبة</SelectItem>
                              <SelectItem value="0.85">85%+</SelectItem>
                              <SelectItem value="0.90">90%+</SelectItem>
                              <SelectItem value="0.95">95%+</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-end gap-2">
                          <Button size="sm" variant="outline" onClick={resetFilters} className="flex-1" data-testid="reset-filters-btn">إعادة ضبط</Button>
                          <Button size="sm" className="btn-cta flex-1" onClick={applyFilters} data-testid="apply-filters-btn">تطبيق</Button>
                        </div>
                      </div>
                    </div>

                    <div className="text-xs text-slate-600 mb-2 flex items-center justify-between">
                      <span data-testid="workshops-count">النتائج: {workshops.length} ورشة</span>
                      {selectedWs.length > 0 && <span className="font-semibold text-brand-amber">مختارة: {selectedWs.length}</span>}
                    </div>

                    <div className="max-h-80 overflow-y-auto space-y-2" data-testid="workshops-results">
                      {wsLoading && <div className="text-center py-6 text-slate-500 text-sm"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
                      {!wsLoading && workshops.map((w) => (
                        <label key={w.id} className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition-colors ${selectedWs.includes(w.id) ? "border-brand-amber bg-amber-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`workshop-row-${w.id}`}>
                          <input type="checkbox" checked={selectedWs.includes(w.id)} className="mt-1"
                            onChange={() => setSelectedWs(selectedWs.includes(w.id) ? selectedWs.filter((x) => x !== w.id) : [...selectedWs, w.id])}
                            data-testid={`select-workshop-${w.id}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="font-semibold text-brand-navy">{w.workshop_name}</div>
                              <div className="flex items-center gap-3 text-xs">
                                <span className="inline-flex items-center gap-1 text-amber-700"><Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" /> {w.quality_rating}/5</span>
                                <span className="inline-flex items-center gap-1 text-emerald-700"><Clock className="w-3.5 h-3.5" /> {(w.on_time_rate * 100).toFixed(0)}%</span>
                              </div>
                            </div>
                            <div className="text-xs text-slate-500 mt-1">{w.city} • {w.industrial_area} • {w.employees} موظف • تأسست {w.year_established}</div>
                            {w.capabilities?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {w.capabilities.slice(0, 5).map((c) => (
                                  <span key={c} className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{c}</span>
                                ))}
                                {w.capabilities.length > 5 && <span className="text-[10px] text-slate-500">+{w.capabilities.length - 5}</span>}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                      {!wsLoading && workshops.length === 0 && (
                        <div className="text-center py-8 text-slate-500 text-sm">لا توجد ورش مطابقة للفلاتر الحالية.</div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                      <Button variant="outline" onClick={() => setRfqDialogOpen(false)}>إلغاء</Button>
                      <Button className="btn-cta" onClick={sendRfq} disabled={!selectedWs.length} data-testid="send-rfq-btn">
                        إرسال RFQ ({selectedWs.length})
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="messages-panel">
            <h3 className="font-bold text-brand-navy mb-4 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> رسائل مع MERQAV</h3>
            <div className="space-y-3 max-h-80 overflow-y-auto mb-3">
              {messages.map((m) => (
                <div key={m.id} className={`rounded-md p-3 text-sm ${m.sender_id === user.id ? "bg-amber-50 ms-8" : "bg-slate-50 me-8"}`}>
                  <div className="font-semibold text-xs text-slate-500 mb-1">{m.sender_name}</div>
                  <div>{m.body}</div>
                </div>
              ))}
              {messages.length === 0 && <div className="text-center text-slate-500 text-sm py-6">لا توجد رسائل بعد.</div>}
            </div>
            <div className="flex gap-2">
              <Input placeholder="اكتب رسالتك..." value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="message-input" />
              <Button onClick={sendMsg} className="btn-cta" data-testid="send-message-btn"><Send className="w-4 h-4" /></Button>
            </div>
            <div className="text-xs text-slate-400 mt-2">لن تُقبل أرقام هواتف أو بريد إلكتروني أو روابط في الرسائل.</div>
          </div>
        </div>
      </div>

      {/* Customer quotation builder dialog */}
      <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>إعداد عرض السعر النهائي للعميل</DialogTitle></DialogHeader>
          {selectedWQ && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
                <div>الورشة المختارة: <span className="font-semibold">{selectedWQ.workshop_name}</span></div>
                <div>تكلفة الورشة: <span className="font-bold">{selectedWQ.total_price?.toLocaleString()} ر.س</span></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>رسوم الفحص</Label><Input type="number" value={quoteForm.inspection_fee} onChange={(e) => setQuoteForm({ ...quoteForm, inspection_fee: e.target.value })} className="mt-1.5" data-testid="qf-inspection" /></div>
                <div><Label>رسوم اللوجستيات</Label><Input type="number" value={quoteForm.logistics_fee} onChange={(e) => setQuoteForm({ ...quoteForm, logistics_fee: e.target.value })} className="mt-1.5" data-testid="qf-logistics" /></div>
                <div><Label>رسوم الخدمة</Label><Input type="number" value={quoteForm.service_fee} onChange={(e) => setQuoteForm({ ...quoteForm, service_fee: e.target.value })} className="mt-1.5" data-testid="qf-service" /></div>
                <div><Label>هامش الربح</Label><Input type="number" value={quoteForm.margin} onChange={(e) => setQuoteForm({ ...quoteForm, margin: e.target.value })} className="mt-1.5" data-testid="qf-margin" /></div>
                <div><Label>صلاحية العرض (أيام)</Label><Input type="number" value={quoteForm.validity_days} onChange={(e) => setQuoteForm({ ...quoteForm, validity_days: e.target.value })} className="mt-1.5" data-testid="qf-validity" /></div>
              </div>
              <div className="bg-brand-navy text-white rounded-md p-4">
                {(() => {
                  const sub = Number(selectedWQ.total_price) + Number(quoteForm.inspection_fee || 0) + Number(quoteForm.logistics_fee || 0) + Number(quoteForm.service_fee || 0) + Number(quoteForm.margin || 0);
                  const vat = sub * 0.15;
                  return (
                    <>
                      <div className="text-sm text-slate-300">المجموع الفرعي: {sub.toLocaleString()} ر.س</div>
                      <div className="text-sm text-slate-300">الضريبة (15%): {vat.toLocaleString()} ر.س</div>
                      <div className="text-2xl font-bold text-brand-amber mt-2">الإجمالي: {(sub + vat).toLocaleString()} ر.س</div>
                    </>
                  );
                })()}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setQuoteDialogOpen(false)}>إلغاء</Button>
                <Button className="btn-cta" onClick={createCustomerQuote} data-testid="submit-customer-quote-btn">إرسال العرض للعميل</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
